import React, { ReactNode } from 'react';
import { useDataView } from './data-view';
import { cn } from '@/lib/utils/cn';

interface DataViewContentProps {
  children?: ReactNode; // Optional direct children override
  
  /**
   * If provided, will automatically render items based on viewMode.
   */
  items?: any[];
  /**
   * Render prop for item in Grid Mode
   */
  renderGridItem?: (item: any, index: number) => ReactNode;
  /**
   * Render prop for item in List Mode
   */
  renderListItem?: (item: any, index: number) => ReactNode;
  
  /**
   * The Skeleton component to use when loading
   */
  loadingIndicator?: ReactNode;
  
  /**
   * The Empty State component to use when empty
   */
  emptyState?: ReactNode;
  
  className?: string;
}

export function DataViewContent({
  items,
  renderGridItem,
  renderListItem,
  loadingIndicator,
  emptyState,
  className,
  children
}: DataViewContentProps) {
  const { viewMode, isLoading, isEmpty } = useDataView();

  if (isLoading && loadingIndicator) {
    return (
      <div className={cn("flex flex-col h-full", className)}>
        {loadingIndicator}
      </div>
    );
  }

  if (isEmpty && !isLoading && emptyState) {
    return (
      <div className={cn("flex flex-col h-full", className)}>
        {emptyState}
      </div>
    );
  }

  if (children) {
    return (
      <div className={cn("space-y-6 min-h-[100vh] lg:min-h-0", className)}>
        {children}
      </div>
    );
  }

  // Auto-rendering logic
  if (!items) return null;

  if (viewMode === 'grid' && renderGridItem) {
    return (
      <div className={cn("space-y-6 min-h-[100vh] lg:min-h-0", className)}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {items.map((item, index) => (
            <React.Fragment key={item.id || index}>
              {renderGridItem(item, index)}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (viewMode === 'list' && renderListItem) {
    return (
      <div className={cn("space-y-6 min-h-[100vh] lg:min-h-0", className)}>
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="divide-y divide-gray-100">
            {items.map((item, index) => (
              <React.Fragment key={item.id || index}>
                {renderListItem(item, index)}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
